import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { EMPTY, Observable, Subject, Subscription, catchError, debounceTime, map, of, switchMap, tap } from 'rxjs';
import { KitchenApiService } from '../api/kitchen-api.service';
import { KitchenStreamService } from '../api/kitchen-stream.service';
import { accionPara, dtoAAgotado, dtoADespachado, dtoAPagina, dtoAPedido } from '../api/kitchen.mapper';
import { ItemActionDto } from '../api/kitchen-api.types';
import {
  CambioEstadoItem,
  CocinaInsumoAgotado,
  CocinaItemDespachado,
  CocinaPedido,
  EstadoItem,
  Pagina,
  paginaVacia,
} from '../models/cocina.model';

const TAMANO_PAGINA_INICIAL = 10;

/**
 * Estado del KDS. La pagina llama a iniciar() al abrirse y a detener() al
 * salir; mientras tanto se mantiene al dia con el stream de cocina.
 */
@Injectable({ providedIn: 'root' })
export class CocinaService {
  private readonly api = inject(KitchenApiService);
  private readonly stream = inject(KitchenStreamService);

  /** Estacion elegida en el selector "Area" ('' = todas). */
  readonly area = signal('');
  readonly pedidos = signal<CocinaPedido[]>([]);
  readonly cargandoPedidos = signal(false);
  readonly errorCarga = signal<string | null>(null);
  readonly despachados = signal<Pagina<CocinaItemDespachado>>(paginaVacia(TAMANO_PAGINA_INICIAL));
  readonly agotados = signal<CocinaInsumoAgotado[]>([]);
  readonly cargandoAgotados = signal(false);
  readonly errorAgotados = signal<string | null>(null);

  readonly pedidosEnEspera = computed(() => this.pedidos().filter((p) => p.estado === 'espera'));
  readonly pedidosEnPreparacion = computed(() => this.pedidos().filter((p) => p.estado === 'preparacion'));

  private readonly recargaPedidos$ = new Subject<void>();
  private readonly recargaDespachados$ = new Subject<void>();
  private readonly recargaAgotados$ = new Subject<void>();
  private suscripcion: Subscription | null = null;

  iniciar(): void {
    if (this.suscripcion) {
      return;
    }
    const suscripcion = new Subscription();

    // switchMap descarta respuestas viejas si llegan recargas seguidas
    suscripcion.add(
      this.recargaPedidos$
        .pipe(
          tap(() => this.cargandoPedidos.set(true)),
          switchMap(() =>
            this.api.listarComandasActivas(this.estacion()).pipe(
              catchError((error) => {
                this.errorCarga.set(mensajeDeError(error));
                this.cargandoPedidos.set(false);
                return EMPTY;
              }),
            ),
          ),
        )
        .subscribe((comandas) => {
          const ahora = Date.now();
          this.pedidos.set(comandas.map((c) => dtoAPedido(c, ahora)));
          this.errorCarga.set(null);
          this.cargandoPedidos.set(false);
        }),
    );

    suscripcion.add(
      this.recargaDespachados$
        .pipe(
          switchMap(() => {
            const actual = this.despachados();
            return this.api.listarDespachados(this.estacion(), actual.pagina, actual.tamano).pipe(
              catchError((error) => {
                console.error('[Cocina] No se pudieron cargar los despachados', error);
                return EMPTY;
              }),
            );
          }),
        )
        .subscribe((pagina) => this.despachados.set(dtoAPagina(pagina, dtoADespachado))),
    );

    // Los agotados vienen de inventario: se piden al abrir la pestana, no con cada aviso de cocina
    suscripcion.add(
      this.recargaAgotados$
        .pipe(
          tap(() => this.cargandoAgotados.set(true)),
          switchMap(() =>
            this.api.listarAgotados().pipe(
              catchError((error) => {
                this.errorAgotados.set(mensajeDeError(error));
                this.cargandoAgotados.set(false);
                return EMPTY;
              }),
            ),
          ),
        )
        .subscribe((agotados) => {
          this.agotados.set(agotados.map(dtoAAgotado));
          this.errorAgotados.set(null);
          this.cargandoAgotados.set(false);
        }),
    );

    // Cualquier aviso de cocina (o una reconexion) recarga todo; los avisos
    // seguidos se agrupan para no disparar una recarga por cada item.
    suscripcion.add(this.stream.eventos(null).pipe(debounceTime(300)).subscribe(() => this.recargarTodo()));

    this.suscripcion = suscripcion;
    this.recargarTodo();
  }

  detener(): void {
    this.suscripcion?.unsubscribe();
    this.suscripcion = null;
  }

  recargarTodo(): void {
    this.recargaPedidos$.next();
    this.recargaDespachados$.next();
  }

  cambiarArea(valor: string): void {
    this.area.set(valor);
    this.despachados.update((p) => ({ ...p, pagina: 0 }));
    this.recargarTodo();
  }

  cargarDespachados(pagina: number, tamano: number): void {
    this.despachados.update((p) => ({ ...p, pagina, tamano }));
    this.recargaDespachados$.next();
  }

  cargarAgotados(): void {
    this.recargaAgotados$.next();
  }

  /**
   * Aplica en un solo lote los estados elegidos en los modales. Se omiten
   * los items que ya estan en ese estado. Devuelve cuantos cambiaron.
   */
  cambiarEstados(cambios: CambioEstadoItem[], despachoDirecto: boolean): Observable<number> {
    const estadoActual = new Map<string, EstadoItem>();
    this.pedidos().forEach((p) => p.items.forEach((i) => estadoActual.set(i.id, i.estado)));

    const changes = cambios
      .filter((c) => estadoActual.get(c.itemId) !== c.estado)
      .map((c) => ({ orderItemId: c.itemId, action: accionPara(c.estado, despachoDirecto) }))
      .filter((c): c is { orderItemId: string; action: ItemActionDto } => c.action !== null);

    if (changes.length === 0) {
      return of(0);
    }
    return this.api.cambiarEstadoItems({ changes }).pipe(
      // Tambien al fallar: si otra pantalla se adelanto, lo que se ve ya no es real
      tap({ next: () => this.recargarTodo(), error: () => this.recargarTodo() }),
      map((resultados) => resultados.filter((r) => r.changed).length),
    );
  }

  /** "A preparacion" desde la pestana Despachados. */
  volverAPreparacion(itemId: string): Observable<void> {
    return this.api.cambiarEstadoItems({ changes: [{ orderItemId: itemId, action: 'IN_PROGRESS' }] }).pipe(
      tap({ next: () => this.recargarTodo(), error: () => this.recargarTodo() }),
      map(() => undefined),
    );
  }

  private estacion(): string | null {
    return this.area() || null;
  }
}

/** Texto para el usuario a partir del error del API (usa el message del backend). */
export function mensajeDeError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      return 'No se pudo conectar con el servicio de cocina';
    }
    const mensaje = (error.error as { message?: string } | null)?.message;
    if (mensaje) {
      return mensaje;
    }
  }
  return 'Ocurrio un error inesperado. Intenta de nuevo';
}
